import {
  type InfrastructureCommand,
  InfrastructureCommandBuilder,
  type InfrastructureResponse,
  InfrastructureResponseBuilder,
} from '@teachino/shared-infrastructure';
import { $t } from '@teachino/shared-i18n';
import type { Course } from '@teachino/shared-legacy-api';
import { useGlobalDependencyProvider } from '@teachino/shared-util';
import { CombinedError, useMutation, useQuery } from '@urql/vue';
import { computed, type ComputedRef, type MaybeRef, ref, type Ref } from 'vue';
import * as z from 'zod';
import { createTopicMutation } from '../create-topic.mutation';
import { createFolderMutation } from './create-folder.mutation';
import { deleteFolderMutation } from './delete-folder.mutation';
import { deleteTopicsMutation } from './delete-topics.mutation';
import { duplicateTopicsToFolderMutation } from './duplicate-topics-to-folder.mutation';
import { convertFolderFragment, type Folder } from './folder.fragment';
import {
  type FolderDetails,
  type FolderSelection,
  type FoldersFacade,
  type FoldersFacadeViewModel,
} from './folders.facade';
import { libraryFolderQuery } from './library-folder.query';
import { libraryFoldersQuery } from './library-folders.query';
import { moveTopicsToFolderMutation } from './move-topics-to-folder.mutation';
import { renameFolderMutation } from './rename-folder.mutation';
import { reorderFolderMutation } from './reorder-folder-mutation';
import { reorderTopicInFolderMutation } from './reorder-topic-in-folder.mutation';
import {
  convertTopicListItemFragment,
  type TopicListItem,
} from './topic-list-item';
import { topicListQuery } from './topics-for-folder.query';

export class FoldersService implements FoldersFacade {
  private globalDi = useGlobalDependencyProvider();
  private analytics = this.globalDi.inject('analytics');

  private folderQueryId = ref<string>();
  private folderSelection: Ref<FolderSelection> = ref('all');
  private queries = {
    folder: useQuery({
      query: libraryFolderQuery,
      pause: true,
      variables: computed(() => {
        return {
          id: this.folderQueryId.value ?? '',
        };
      }),
    }),
    viewModel: useQuery({
      query: libraryFoldersQuery,
      pause: true,
      variables: computed(() => {
        if (this.folderSelection.value === 'all') {
          return { selectedFolderId: undefined };
        }
        if (this.folderSelection.value === 'unsorted') {
          return { selectedFolderId: null };
        }
        return { selectedFolderId: this.folderSelection.value.folderId };
      }),
    }),
  };

  private mutations = {
    moveTopicsToFolder: useMutation(moveTopicsToFolderMutation),
    duplicateTopicsToFolder: useMutation(duplicateTopicsToFolderMutation),
    reorderTopicInFolder: useMutation(reorderTopicInFolderMutation),
    reorderFolder: useMutation(reorderFolderMutation),
    renameFolder: useMutation(renameFolderMutation),
    deleteFolder: useMutation(deleteFolderMutation),
    createFolder: useMutation(createFolderMutation),
    deleteTopics: useMutation(deleteTopicsMutation),
    createTopic: useMutation(createTopicMutation),
  };

  viewModel(): InfrastructureResponse<FoldersFacadeViewModel> {
    return InfrastructureResponseBuilder.fromQuery(this.queries.viewModel)
      .withMapper((result) => {
        const allFolders = result.libraryFolders.map(convertFolderFragment);

        const topicsForSelection = result.searchContent
          .filter((content) => content.__typename === 'Folder')
          .map(convertTopicListItemFragment);

        let selectedFolder = null;
        let selectionHeader = '';

        const selection = this.folderSelection.value;
        if (selection !== 'all' && selection !== 'unsorted') {
          selectedFolder =
            allFolders.find((f) => f.id === selection.folderId) ?? null;
          selectionHeader = selectedFolder?.name ?? '';
        }
        if (selection === 'all') {
          selectionHeader = $t('generic_topics');
        }
        if (selection === 'unsorted') {
          selectionHeader = $t('library.unsorted_topics');
        }

        const newestMaterials = result.libraryNewestContent.materials;

        return {
          selectionHeader,
          allFolders,
          totalTopicsCount: result.libraryMeta.totalTopicsCount,
          unsortedTopicsCount: result.libraryMeta.unsortedTopicsCount,
          topicsForSelection,
          currentSelection: this.folderSelection.value,
          selectedFolder,
          newestMaterials,
        };
      })
      .build();
  }

  filteredAndSortedRootFolders(): ComputedRef<Folder[]> {
    return computed(() => {
      const allFolders = this.viewModel().data.value?.allFolders ?? [];
      return allFolders
        .filter((libraryFolder) => {
          return libraryFolder.parentFolder === null;
        })
        .sort((libraryFolderA, libraryFolderB) => {
          return libraryFolderA.position - libraryFolderB.position;
        });
    });
  }

  folder(id: string): InfrastructureResponse<FolderDetails> {
    this.folderQueryId.value = id;

    return InfrastructureResponseBuilder.fromQuery(this.queries.folder)
      .withMapper((data) => {
        const folder = convertFolderFragment(data.libraryFolder);
        const topics = data.searchContent
          .filter((content) => content.__typename === 'Folder')
          .map(convertTopicListItemFragment);

        const hasTopics =
          data.searchContent.length && data.searchContent.length > 0;
        const hasChildren = folder.children.length > 0;
        const isDeletable = !hasTopics && !hasChildren;
        return {
          ...folder,
          topics,
          isDeletable,
        };
      })
      .build();
  }
  moveTopicsToFolder(args: {
    topicIds: string[];
    folderId: string;
  }): InfrastructureCommand<void> {
    return InfrastructureCommandBuilder.fromMutation(
      this.mutations.moveTopicsToFolder,
      args,
    )
      .withFetching((fetching) =>
        computed(() => fetching.value || this.queries.viewModel.fetching.value),
      )
      .withSideeffect(({ reject }) => {
        this.loadViewModel().catch(reject);
      })
      .build();
  }

  duplicateTopicsToFolder(args: {
    topicIds: string[];
    folderId: string;
  }): InfrastructureCommand<void> {
    return InfrastructureCommandBuilder.fromMutation(
      this.mutations.duplicateTopicsToFolder,
      args,
    )
      .withSideeffect(({ reject }) => {
        this.loadViewModel().catch(reject);
      })
      .withFetching((fetching) =>
        computed(() => fetching.value || this.queries.viewModel.fetching.value),
      )
      .build();
  }

  reorderTopicInFolder(args: {
    topicId: string;
    position: number;
    folderId: string | null;
  }): InfrastructureCommand<void> {
    return InfrastructureCommandBuilder.fromMutation(
      this.mutations.reorderTopicInFolder,
      args,
    )
      .withSideeffect(({ reject }) => {
        this.loadViewModel().catch(reject);
      })
      .build();
  }

  selectFolder(selection: FolderSelection): void {
    this.folderSelection.value = selection;
    this.loadViewModel().catch((err: unknown) => console.error(err));
  }

  topicsForFolder(folderId: string): InfrastructureResponse<TopicListItem[]> {
    const query = useQuery({
      query: topicListQuery,
      pause: true,
      variables: {
        folderId,
      },
    });

    return InfrastructureResponseBuilder.fromQuery(query)
      .withMapper((response) => {
        return response.searchContent
          .filter((content) => content.__typename === 'Folder')
          .map(convertTopicListItemFragment);
      })
      .build();
  }

  createFolder(args: {
    name: string;
    parentFolderId: string | null;
  }): InfrastructureCommand<{ id: string }> {
    const { name, parentFolderId } = args;
    return InfrastructureCommandBuilder.fromMutation(
      this.mutations.createFolder,
      { name, parentFolderId },
    )
      .withMapper((result) => {
        return { id: result.createLibraryFolder.libraryFolder.id };
      })

      .withSideeffect(({ reject }) => {
        this.analytics.track('library-folder-created');
        this.loadViewModel().catch(reject);
      })
      .build();
  }

  deleteFolder(id: string): InfrastructureCommand<void> {
    return InfrastructureCommandBuilder.fromMutation(
      this.mutations.deleteFolder,
      { id },
    )
      .withSideeffect(({ reject }) => {
        this.loadViewModel().catch(reject);
      })
      .build();
  }

  renameFolder(args: {
    id: MaybeRef<string>;
    name: MaybeRef<string>;
  }): InfrastructureCommand<void> {
    return InfrastructureCommandBuilder.fromMutation(
      this.mutations.renameFolder,
      args,
    )
      .withSideeffect(({ reject }) => {
        this.loadViewModel().catch(reject);
      })
      .build();
  }

  reorderFolder(args: {
    folderId: string;
    newParentFolderId: string | null;
    position: number;
  }): InfrastructureCommand<void> {
    return InfrastructureCommandBuilder.fromMutation(
      this.mutations.reorderFolder,
      args,
    )
      .withSideeffect(({ reject }) => {
        this.loadViewModel().catch(reject);
      })
      .build();
  }

  async loadViewModel(): Promise<void> {
    await this.queries.viewModel.executeQuery();
  }

  createTopic(args: {
    name: string;
    goals: string | null;
    folderId: string | null;
  }): InfrastructureCommand<TopicListItem> {
    return InfrastructureCommandBuilder.fromMutation(
      this.mutations.createTopic,
      args,
    )
      .withMapper((result) => {
        return convertTopicListItemFragment(result.createTopic.topic);
      })
      .withSideeffect(({ reject }) => {
        this.loadViewModel().catch(reject);
      })
      .build();
  }

  deleteTopics(
    topicIds: string[],
  ): InfrastructureCommand<void, { affectedCourses: Course[] }> {
    return InfrastructureCommandBuilder.fromMutation(
      this.mutations.deleteTopics,
      { topicIds, force: false },
    )
      .withErrorMapper((error) => {
        return this.parseDeleteTopicError(error);
      })
      .withSideeffect(({ reject }) => {
        this.loadViewModel().catch(reject);
      })
      .withFetching((fetching) =>
        computed(() => fetching.value || this.queries.viewModel.fetching.value),
      )
      .build();
  }

  private parseDeleteTopicError(error: CombinedError): {
    affectedCourses: Course[];
  } {
    if (error.graphQLErrors.length === 0) {
      throw new Error('Graphql Error thrown with 0-length Error array');
    }
    const [firstError] = error.graphQLErrors;
    const extensions = firstError.extensions;
    const extensionSchema = z.object({
      myExtensions: z.object({
        data: z.array(
          z.object({
            id: z.string().min(1),
            shortName: z.string().nullable(),
            name: z.string().min(1),
            foregroundColor: z.string().min(1),
            backgroundColor: z.string().min(1),
            __typename: z.literal('Course'),
          }),
        ),
      }),
    });
    const parsedExtensions = extensionSchema.safeParse(extensions);
    if (parsedExtensions.success) {
      const courses = parsedExtensions.data.myExtensions.data;
      const courseMap = new Map<string, Course>();
      courses.forEach((course) => courseMap.set(course.id, course));
      return { affectedCourses: [...courseMap.values()] };
    } else {
      console.error(parsedExtensions.error);
      return { affectedCourses: [] };
    }
  }

  confirmTopicDeletion(topicIds: string[]): InfrastructureCommand<void> {
    return InfrastructureCommandBuilder.fromMutation(
      this.mutations.deleteTopics,
      { topicIds, force: true },
    )
      .withSideeffect(({ reject }) => {
        this.loadViewModel().catch(reject);
      })
      .build();
  }
}
